import { createFileRoute } from '@tanstack/react-router';
import { CodeBlock, DocTable, InfoCallout } from '../../components/documentation';
import styles from '../../components/documentation/DocContent.module.css';

export const Route = createFileRoute('/documentation/local-agent')({
  component: LocalAgentPage,
});

function LocalAgentPage() {
  return (
    <>
      <div className={styles.breadcrumb}>Getting Started</div>
      <h1 className={styles.pageTitle}>Local Agent</h1>
      <p className={styles.pageSubtitle}>
        Run your Unreal Engine or Unity application on your own machine and
        connect it to the Interlucent streaming platform. Iterate locally without
        waiting for a cloud deployment cycle.
      </p>

      <section className={styles.section}>
        <p className={styles.prose}>
          The Local Agent lets you run your GPU-rendered application on your own
          machine and connect it to the Interlucent streaming platform. Instead of
          deploying to the cloud every time you make a change, you work locally —
          the agent handles all the signaling and session management behind the
          scenes.
        </p>
        <p className={styles.prose}>
          This is useful in two main scenarios:
        </p>
        <ul className={styles.featureList}>
          <li className={styles.featureItem}>
            <span className={styles.featureLabel}>Game Developers</span>{' '}
            iterating on their streaming application. You can tweak shaders,
            adjust input handling, or test Pixel Streaming features and
            immediately see how they behave in a browser-delivered stream —
            without waiting for a cloud deployment cycle.
          </li>
          <li className={styles.featureItem}>
            <span className={styles.featureLabel}>Web Developers</span>{' '}
            building an experience around a stream. You get a live stream to
            develop against without burning cloud GPU hours. The stream behaves
            exactly as it would in production, so your frontend code works the
            same way when you switch to cloud workers.
          </li>
        </ul>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="how-it-works">
          How It Works
        </h2>
        <p className={styles.prose}>
          The local agent acts as a bridge between your locally-running
          application and the Interlucent Marketplace. When you start it:
        </p>
        <ol className={styles.stepList}>
          <li className={styles.stepItem}>
            <span className={styles.stepNumber}>1</span>
            <span>
              It exchanges your admission token for access credentials.
            </span>
          </li>
          <li className={styles.stepItem}>
            <span className={styles.stepNumber}>2</span>
            <span>
              It starts a WebSocket signaling server on your machine (default:
              port 8080).
            </span>
          </li>
          <li className={styles.stepItem}>
            <span className={styles.stepNumber}>3</span>
            <span>
              It connects to the Marketplace and waits for a session assignment.
            </span>
          </li>
          <li className={styles.stepItem}>
            <span className={styles.stepNumber}>4</span>
            <span>
              When a user connects through the platform, the agent bridges WebRTC
              signaling between the browser and your local app.
            </span>
          </li>
        </ol>
        <InfoCallout variant="info">
          The agent does <strong>not</strong> launch your application — you start
          your game or app yourself and point it at the local signaling server.
          This means you keep full control over your dev workflow: use your IDE's
          debugger, hot-reload, restart at will.
        </InfoCallout>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="installation">
          Installation
        </h2>

        <h3 className={styles.subsectionTitle} id="install-linux-macos">
          Linux / macOS
        </h3>
        <CodeBlock language="bash">
          {`curl -fsSL https://cdn.interlucent.ai/tools/tools.sh | bash`}
        </CodeBlock>

        <h3 className={styles.subsectionTitle} id="install-windows">
          Windows (PowerShell)
        </h3>
        <CodeBlock language="powershell">
          {`irm https://cdn.interlucent.ai/tools/tools.ps1 | iex`}
        </CodeBlock>
        <p className={styles.prose}>
          This installs the{' '}
          <code className={styles.mono}>local-agent</code> binary and makes it
          available on your PATH.
        </p>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="quick-start">
          Quick Start
        </h2>

        <h3 className={styles.subsectionTitle} id="get-token">
          1. Get an admission token
        </h3>
        <p className={styles.prose}>
          You'll need an admission token (JWT) from the Interlucent dashboard or
          your team lead. The token identifies which application(s) you're
          allowed to serve.
        </p>
        <p className={styles.prose}>
          The recommended approach is to store the token in an environment
          variable so you don't have to paste it into every command:
        </p>

        <CodeBlock language="bash" title="Linux / macOS (bash/zsh)">
          {`export ADMISSION_TOKEN="eyJhbGciOi..."`}
        </CodeBlock>
        <CodeBlock language="powershell" title="Windows (PowerShell)">
          {`$env:ADMISSION_TOKEN = "eyJhbGciOi..."`}
        </CodeBlock>

        <InfoCallout variant="tip">
          For persistent storage across terminal sessions, add the export to your
          shell profile (<code className={styles.mono}>~/.bashrc</code>,{' '}
          <code className={styles.mono}>~/.zshrc</code>) or use your system's
          environment variable settings on Windows. Avoid committing tokens to
          source control.
        </InfoCallout>

        <h3 className={styles.subsectionTitle} id="start-app">
          2. Start your application
        </h3>
        <p className={styles.prose}>
          Launch your UE or Unity application with Pixel Streaming enabled.
          Configure it to connect to the local signaling server:
        </p>
        <CodeBlock language="bash" title="Unreal Engine">
          {`MyGame.exe -PixelStreamingURL=ws://127.0.0.1:8080`}
        </CodeBlock>

        <h3 className={styles.subsectionTitle} id="start-agent">
          3. Start the local agent
        </h3>
        <CodeBlock language="bash" title="Linux / macOS">
{`export ADMISSION_TOKEN="eyJhbGciOi..."
local-agent`}
        </CodeBlock>
        <CodeBlock language="powershell" title="Windows (PowerShell)">
{`$env:ADMISSION_TOKEN = "eyJhbGciOi..."
local-agent`}
        </CodeBlock>

        <p className={styles.prose}>
          That's it. The agent connects to the Marketplace, and when a browser
          session is routed to you, it bridges the signaling to your local app.
          You'll see log output confirming each phase:
        </p>
        <CodeBlock language="text">
{`INFO Starting local agent  app_id=PFfFuw version_id=MrM signaling_port=8080
INFO Signaling server listening — start your UE app and connect to ws://127.0.0.1:8080
INFO Joined marketplace cell — waiting for jobs`}
        </CodeBlock>
        <p className={styles.prose}>
          The order doesn't matter — you can start the agent before or after your
          application. The signaling server waits patiently for the streamer to
          connect.
        </p>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="cli-arguments">
          Command-Line Arguments
        </h2>
        <p className={styles.prose}>
          All arguments can also be set via environment variables. Environment
          variables are useful for CI/CD scripts, Docker containers, or just
          keeping your terminal commands short.
        </p>
        <DocTable
          columns={[
            { header: 'Argument', accessor: 'argument', mono: true, nowrap: true },
            { header: 'Env Variable', accessor: 'env', mono: true, nowrap: true },
            { header: 'Default', accessor: 'default' },
            { header: 'Description', accessor: 'description' },
          ]}
          data={[
            {
              argument: '--admission-token',
              env: 'ADMISSION_TOKEN',
              default: '(required)',
              description: 'JWT admission token for authentication',
            },
            {
              argument: '--api-base-url',
              env: 'API_BASE_URL',
              default: 'https://api.interlucent.ai',
              description: 'API endpoint URL',
            },
            {
              argument: '--app-id',
              env: 'APP_ID',
              default: '(from token)',
              description: 'Application ID (required if token lacks allowed_apps)',
            },
            {
              argument: '--version-id',
              env: 'VERSION_ID',
              default: '(from token)',
              description: 'Version ID (required if token lacks allowed_apps)',
            },
            {
              argument: '--signaling-port',
              env: 'SIGNALING_PORT',
              default: '8080',
              description: 'Port for the local WebSocket signaling server',
            },
            {
              argument: '--streamer-rendezvous-ms',
              env: 'STREAMER_RENDEZVOUS_MS',
              default: '0 (infinite)',
              description: 'How long to wait for the UE streamer to connect before giving up. 0 means wait forever.',
            },
            {
              argument: '--lat-override',
              env: 'LAT_OVERRIDE',
              default: '(none)',
              description: 'Latitude override for marketplace placement',
            },
            {
              argument: '--lng-override',
              env: 'LNG_OVERRIDE',
              default: '(none)',
              description: 'Longitude override for marketplace placement',
            },
            {
              argument: '--log-level',
              env: 'LOG_LEVEL',
              default: 'info',
              description: 'Log verbosity: trace, debug, info, warn, error',
            },
            {
              argument: '--log-format',
              env: 'LOG_FORMAT',
              default: 'compact',
              description: 'Log output format: compact, json, pretty, full',
            },
          ]}
        />

        <h3 className={styles.subsectionTitle} id="app-version-ids">
          App ID and Version ID
        </h3>
        <p className={styles.prose}>
          If your admission token contains an{' '}
          <code className={styles.mono}>allowed_apps</code> claim (in the{' '}
          <code className={styles.mono}>wrc</code> / worker constraints field),
          the agent extracts the app ID and version ID automatically — you don't
          need to specify{' '}
          <code className={styles.mono}>--app-id</code> or{' '}
          <code className={styles.mono}>--version-id</code>.
        </p>
        <p className={styles.prose}>
          If the token does not contain{' '}
          <code className={styles.mono}>allowed_apps</code>, both arguments are
          required:
        </p>
        <CodeBlock language="bash">
          {`local-agent --app-id PFfFuw --version-id MrM`}
        </CodeBlock>
        <p className={styles.prose}>
          When the token <em>does</em> contain{' '}
          <code className={styles.mono}>allowed_apps</code> and you also pass{' '}
          <code className={styles.mono}>--app-id</code>/<code className={styles.mono}>--version-id</code>,
          the token values take precedence and the CLI arguments are ignored
          (with a warning).
        </p>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="practical-examples">
          Practical Examples
        </h2>

        <h3 className={styles.subsectionTitle} id="example-game-dev">
          Game Developer: Iterating on Pixel Streaming Features
        </h3>
        <p className={styles.prose}>
          You're working on input handling for a UE5 project and want to test how
          touch events translate through the stream:
        </p>
        <CodeBlock language="bash" title="Linux / macOS">
{`# Terminal 1: Start your UE app (editor or packaged build)
cd /path/to/MyProject
./MyProject.sh -PixelStreamingURL=ws://127.0.0.1:8080

# Terminal 2: Run the local agent
export ADMISSION_TOKEN="eyJhbGciOi..."
local-agent --log-level debug`}
        </CodeBlock>
        <CodeBlock language="powershell" title="Windows (PowerShell)">
{`# Terminal 1: Start your UE app
& "C:\\Projects\\MyProject\\MyProject.exe" -PixelStreamingURL=ws://127.0.0.1:8080

# Terminal 2: Run the local agent
$env:ADMISSION_TOKEN = "eyJhbGciOi..."
local-agent --log-level debug`}
        </CodeBlock>
        <p className={styles.prose}>
          With <code className={styles.mono}>--log-level debug</code> you get
          detailed signaling messages, which is helpful when diagnosing WebRTC
          negotiation issues.
        </p>

        <h3 className={styles.subsectionTitle} id="example-web-dev">
          Web Developer: Building a Frontend Against a Live Stream
        </h3>
        <p className={styles.prose}>
          You're building a React app that wraps a pixel stream. You need a real
          stream to develop against, but don't want to spin up cloud resources:
        </p>
        <CodeBlock language="bash" title="Linux / macOS">
{`# Terminal 1: UE app running on your machine or a colleague's machine on LAN
./MyProject.sh -PixelStreamingURL=ws://127.0.0.1:8080

# Terminal 2: Local agent
export ADMISSION_TOKEN="eyJhbGciOi..."
export API_BASE_URL="https://api.interlucent.ai"
local-agent

# Terminal 3: Your frontend dev server
cd my-web-app
npm run dev`}
        </CodeBlock>
        <CodeBlock language="powershell" title="Windows (PowerShell)">
{`# Terminal 1
& "C:\\Projects\\MyProject\\MyProject.exe" -PixelStreamingURL=ws://127.0.0.1:8080

# Terminal 2
$env:ADMISSION_TOKEN = "eyJhbGciOi..."
$env:API_BASE_URL = "https://api.interlucent.ai"
local-agent

# Terminal 3
cd my-web-app
npm run dev`}
        </CodeBlock>
        <p className={styles.prose}>
          Your frontend connects to the platform the same way it would in
          production. When a session is routed to your local agent, the stream
          appears in your browser just as it would from a cloud worker.
        </p>

        <InfoCallout variant="tip">
          Your frontend code uses the same{' '}
          <code className={styles.mono}>&lt;pixel-stream&gt;</code> component and
          admission tokens as a production deployment. See the{' '}
          <code className={styles.mono}>pixel-stream/examples/</code> directory
          for standalone HTML overlays you can open directly against your local
          agent.
        </InfoCallout>

        <h3 className={styles.subsectionTitle} id="example-custom-port">
          Custom Signaling Port
        </h3>
        <p className={styles.prose}>
          If port 8080 conflicts with another service on your machine:
        </p>
        <CodeBlock language="bash">
          {`local-agent --signaling-port 9090`}
        </CodeBlock>
        <p className={styles.prose}>
          Then launch your UE app with{' '}
          <code className={styles.mono}>
            -PixelStreamingURL=ws://127.0.0.1:9090
          </code>.
        </p>

        <h3 className={styles.subsectionTitle} id="example-env-file">
          Using a .env File
        </h3>
        <p className={styles.prose}>
          For convenience, you can keep your configuration in a file and source
          it before running:
        </p>
        <CodeBlock language="bash" title="local-agent.env">
{`export ADMISSION_TOKEN="eyJhbGciOi..."
export API_BASE_URL="https://api.interlucent.ai"
export SIGNALING_PORT="8080"
export LOG_LEVEL="info"`}
        </CodeBlock>
        <CodeBlock language="bash">
{`source local-agent.env
local-agent`}
        </CodeBlock>

        <CodeBlock language="powershell" title="local-agent.ps1">
{`$env:ADMISSION_TOKEN = "eyJhbGciOi..."
$env:API_BASE_URL = "https://api.interlucent.ai"
$env:SIGNALING_PORT = "8080"
$env:LOG_LEVEL = "info"
local-agent`}
        </CodeBlock>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="lifecycle">
          Lifecycle
        </h2>
        <p className={styles.prose}>
          The agent runs in a continuous loop:
        </p>
        <div className={styles.timeline}>
          <pre className={styles.timelinePre}>{`Starting ─── AwaitingJob ⇄ Serving
                  ↓              ↓
             ShuttingDown ← ShuttingDown`}</pre>
        </div>
        <div className={styles.conceptList}>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>Starting</div>
            <div className={styles.conceptDef}>
              Exchanges the admission token, starts the signaling server.
            </div>
          </div>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>AwaitingJob</div>
            <div className={styles.conceptDef}>
              Connected to the Marketplace, waiting for a session to be routed to
              you. Your UE app can connect to the signaling server at any point
              during this phase.
            </div>
          </div>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>Serving</div>
            <div className={styles.conceptDef}>
              A browser session is active. The agent bridges signaling between
              the browser (via SessionDO) and your local app.
            </div>
          </div>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>ShuttingDown</div>
            <div className={styles.conceptDef}>
              Triggered by Ctrl+C (SIGINT/SIGTERM). Cleanly closes marketplace
              and session connections.
            </div>
          </div>
        </div>
        <p className={styles.prose}>
          After a session ends (the browser disconnects, or you stop the UE app),
          the agent returns to <strong>AwaitingJob</strong> and is ready for the
          next session. You don't need to restart it between sessions.
        </p>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="token-handling">
          Token Handling
        </h2>
        <p className={styles.prose}>
          The agent handles token lifecycle automatically:
        </p>
        <ul className={styles.featureList}>
          <li className={styles.featureItem}>
            On startup, the admission token is exchanged for a short-lived access
            token.
          </li>
          <li className={styles.featureItem}>
            If the access token expires mid-session, the agent detects the 401
            response, refreshes the token using the original admission token, and
            reconnects without interruption.
          </li>
          <li className={styles.featureItem}>
            The admission token itself has a longer lifetime. If it expires,
            you'll need a new one from the dashboard.
          </li>
        </ul>
        <InfoCallout variant="warning">
          Keep your admission token private. Don't commit it to version control
          or share it in chat. Environment variables or a{' '}
          <code className={styles.mono}>.env</code> file (excluded from git via{' '}
          <code className={styles.mono}>.gitignore</code>) are the safest
          approach for local development.
        </InfoCallout>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="troubleshooting">
          Troubleshooting
        </h2>
        <div className={styles.conceptList}>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>"Missing --app-id"</div>
            <div className={styles.conceptDef}>
              Your admission token doesn't include{' '}
              <code className={styles.mono}>allowed_apps</code>. Pass{' '}
              <code className={styles.mono}>--app-id</code> and{' '}
              <code className={styles.mono}>--version-id</code> explicitly, or
              ask your admin for a token with the apps baked in.
            </div>
          </div>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>
              "Streamer rendezvous timeout"
            </div>
            <div className={styles.conceptDef}>
              The UE app didn't connect to the signaling server in time. By
              default the local agent waits forever
              (<code className={styles.mono}>--streamer-rendezvous-ms=0</code>),
              but if you've set a timeout, make sure your app is configured to
              connect to the correct{' '}
              <code className={styles.mono}>ws://127.0.0.1:&lt;port&gt;</code>.
            </div>
          </div>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>Port already in use</div>
            <div className={styles.conceptDef}>
              Another process is using port 8080. Use{' '}
              <code className={styles.mono}>--signaling-port</code> to pick a
              different port, and update your UE launch args to match.
            </div>
          </div>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>
              "Access token expired — attempting refresh"
            </div>
            <div className={styles.conceptDef}>
              Normal operation. The agent refreshes automatically. If refresh
              fails repeatedly, your admission token may have expired — get a
              new one.
            </div>
          </div>
          <div className={styles.conceptItem}>
            <div className={styles.conceptTerm}>
              Connection drops / reconnects
            </div>
            <div className={styles.conceptDef}>
              The agent automatically reconnects to the Marketplace if the
              WebSocket connection drops. You'll see a "Marketplace disconnected
              — retrying" message followed by a successful reconnect.
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
